import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../utils/axios";

const section = () => ({ data: null, loading: false, error: null });

// --- Async Thunks ---

export const generatePackages = createAsyncThunk(
  "ai/generatePackages",
  async ({ serviceType, serviceData, questionnaire }, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/api/vendor/ai/generate-packages", { serviceType, serviceData, questionnaire });
      return data.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message);
    }
  }
);

export const analyzeFile = createAsyncThunk(
  "ai/analyzeFile",
  async ({ file, serviceType }, { rejectWithValue }) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("serviceType", serviceType);
      const { data } = await api.post("/api/vendor/ai/analyze-file", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message);
    }
  }
);

export const suggestPricing = createAsyncThunk(
  "ai/suggestPricing",
  async ({ serviceType, serviceData }, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/api/vendor/ai/suggest-pricing", { serviceType, serviceData });
      return data.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message);
    }
  }
);

export const applyPackagesToService = createAsyncThunk(
  "ai/applyPackagesToService",
  async ({ serviceType, serviceId, packages, updatePricing = false }, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/api/vendor/ai/apply-packages", { serviceType, serviceId, packages, updatePricing });
      return data.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message);
    }
  }
);

export const getLeadScores = createAsyncThunk(
  "ai/getLeadScores",
  async (status = "pending", { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/api/vendor/ai/lead-scores?status=${status}`);
      return data.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message);
    }
  }
);

export const getColdLeads = createAsyncThunk(
  "ai/getColdLeads",
  async (hours = 6, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/api/vendor/ai/cold-leads?hours=${hours}`);
      return data.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message);
    }
  }
);

export const verifyBusiness = createAsyncThunk(
  "ai/verifyBusiness",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/api/vendor/ai/verify-business", payload);
      return data.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message);
    }
  }
);

export const readDocument = createAsyncThunk(
  "ai/readDocument",
  async ({ file, documentType }, { rejectWithValue }) => {
    try {
      const fd = new FormData();
      fd.append("document", file);
      fd.append("documentType", documentType);
      const { data } = await api.post("/api/vendor/ai/read-document", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message);
    }
  }
);

export const getAutoReplySettings = createAsyncThunk(
  "ai/getAutoReplySettings",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/api/vendor/ai/auto-reply-settings");
      return data.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message);
    }
  }
);

export const updateAutoReplySettings = createAsyncThunk(
  "ai/updateAutoReplySettings",
  async (settings, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/api/vendor/ai/auto-reply-settings", settings);
      return data.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message);
    }
  }
);

export const getProfileScore = createAsyncThunk(
  "ai/getProfileScore",
  async ({ serviceType, serviceId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/api/vendor/ai/profile-score/${serviceType}/${serviceId}`);
      return data.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message);
    }
  }
);

// --- Helpers to build repetitive cases ---
const addThunkCases = (builder, thunk, key) => {
  builder
    .addCase(thunk.pending, (state) => {
      state[key].loading = true;
      state[key].error = null;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state[key].loading = false;
      state[key].data = action.payload;
    })
    .addCase(thunk.rejected, (state, action) => {
      state[key].loading = false;
      state[key].error = action.payload;
    });
};

const initialState = {
  packages: section(),
  pricingSuggestions: section(),
  leadScores: section(),
  coldLeads: section(),
  verification: section(),
  documentRead: section(),
  autoReply: section(),
  profileScore: section(),
  fileAnalysis: section(),
};

const aiSlice = createSlice({
  name: "ai",
  initialState,
  reducers: {
    clearAiSection: (state, action) => {
      const key = action.payload;
      if (state[key]) {
        state[key] = section();
      }
    },
  },
  extraReducers: (builder) => {
    addThunkCases(builder, generatePackages, "packages");
    addThunkCases(builder, analyzeFile, "fileAnalysis");
    addThunkCases(builder, suggestPricing, "pricingSuggestions");
    addThunkCases(builder, getLeadScores, "leadScores");
    addThunkCases(builder, getColdLeads, "coldLeads");
    addThunkCases(builder, verifyBusiness, "verification");
    addThunkCases(builder, readDocument, "documentRead");
    addThunkCases(builder, getAutoReplySettings, "autoReply");
    addThunkCases(builder, updateAutoReplySettings, "autoReply");
    addThunkCases(builder, getProfileScore, "profileScore");
  },
});

export const { clearAiSection } = aiSlice.actions;

export const selectPackages = (s) => s.ai.packages;
export const selectPricingSuggestions = (s) => s.ai.pricingSuggestions;
export const selectLeadScores = (s) => s.ai.leadScores;
export const selectColdLeads = (s) => s.ai.coldLeads;
export const selectVerification = (s) => s.ai.verification;
export const selectDocumentRead = (s) => s.ai.documentRead;
export const selectAutoReply = (s) => s.ai.autoReply;
export const selectProfileScore = (s) => s.ai.profileScore;
export const selectFileAnalysis = (s) => s.ai.fileAnalysis;

export default aiSlice.reducer;
